import {useTranslation} from "react-i18next";
import {formatPhoneWithDialCode} from "../../../helpers/phone";
import "./ReadonlyOrderSummaries.scss";

interface ReadonlyDeliverySummaryProps {
    // addressData: AddressData | null;
    addressData: {
        full_address: string;
        postal_code: string;
        house_number: string;
        apartment_door: string | null;
        entrance: string | null;
        floor: string | null;
        address_extra_details: string | null;
    } | null;
    description: string;
    fullName: string;
    email: string;
    phone: {
        dialCode: string;
        phoneNumber: string;
    } | undefined;
    pickupTime?: string | null;
    dropOffTime?: string;
};

interface ReadonlyPickupSummaryProps {
    email: string;
    phone: {
        dialCode: string;
        phoneNumber: string;
    } | undefined;
    pickupTime?: string;
};

export const ReadonlyDeliverySummary = ({
    addressData,
    description = "",
    fullName = "",
    email = "",
    phone,
    pickupTime = "",
    dropOffTime = "",
}: ReadonlyDeliverySummaryProps) => {
    const {
        full_address: address = "",
        postal_code: postalCode = "",
        house_number: houseNumber = "",
        apartment_door: doorNumber = "",
        entrance: apartmentEntrance = "",
        floor = "",
        address_extra_details: extraAddress = "",
    } = addressData || {};

    const {
        dialCode = "",
        phoneNumber = "",
    } = phone || {};

    const {t} = useTranslation();

    const displayPhoneNumber = formatPhoneWithDialCode(dialCode, phoneNumber, "- - -");

    const addressMetaParts = [
        postalCode ? `${t("postalCode")}: ${postalCode}` : "",
        houseNumber ? `${t("houseNumber")}: ${houseNumber}` : "",
        doorNumber ? `${t("doorNumber")}: ${doorNumber}` : "",
        apartmentEntrance ? `${t("apartmentEntrance")}: ${apartmentEntrance}` : "",
        floor ? `${t("floor")}: ${floor}` : "",
    ].filter(Boolean);


    return (
        <div className="delivery-readonly-summary mb-3">
            <div className="delivery-readonly-summary-title mb-3">
                {t("deliveryDetails")}
            </div>

            <div className="delivery-readonly-summary-section">
                <div className="delivery-readonly-summary-subtitle">{t("deliveryAddress")}</div>
                <div className="delivery-readonly-summary-grid">
                    <div className="delivery-readonly-summary-item delivery-readonly-summary-item-full">
                        <span className="delivery-readonly-summary-label">{t("fullAddress")}</span>
                        <span className="delivery-readonly-summary-value">
                            {address || "- - -"}
                            {addressMetaParts.length > 0 && (
                                <small className="delivery-readonly-summary-meta">
                                    {addressMetaParts.join("  •  ")}
                                </small>
                            )}
                        </span>
                    </div>
                    <div className="delivery-readonly-summary-item delivery-readonly-summary-item-full">
                        <span className="delivery-readonly-summary-label">{t("extraAddressDetails")}</span>
                        <span className="delivery-readonly-summary-value">{extraAddress || "- - -"}</span>
                    </div>
                </div>
            </div>

            {(pickupTime || dropOffTime) && (
                <div className="delivery-readonly-summary-section mt-3">
                    <div className="delivery-readonly-summary-subtitle">{t("orderTiming")}</div>
                    <div className="delivery-readonly-summary-grid">
                        {pickupTime && (
                            <div className="delivery-readonly-summary-item">
                                <span className="delivery-readonly-summary-label">{t("pickupTime")}</span>
                                <span className="delivery-readonly-summary-value">
                                    {pickupTime}
                                </span>
                            </div>
                        )}
                        {dropOffTime && (
                            <div className="delivery-readonly-summary-item">
                                <span className="delivery-readonly-summary-label">{t("dropOffTime")}</span>
                                <span className="delivery-readonly-summary-value">
                                    {dropOffTime}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="delivery-readonly-summary-section mt-3">
                <div className="delivery-readonly-summary-subtitle">
                    {t("contactInformationAtPickup")}
                </div>
                <div className="delivery-readonly-summary-grid">
                    <div className="delivery-readonly-summary-item">
                        <span className="delivery-readonly-summary-label">{t("fullName")}</span>
                        <span className="delivery-readonly-summary-value">{fullName || "- - -"}</span>
                    </div>
                    <div className="delivery-readonly-summary-item">
                        <span className="delivery-readonly-summary-label">{t("email")}</span>
                        <span className="delivery-readonly-summary-value">{email || "- - -"}</span>
                    </div>
                    <div className="delivery-readonly-summary-item delivery-readonly-summary-item-full">
                        <span className="delivery-readonly-summary-label">{t("phoneNumber")}</span>
                        <span className="delivery-readonly-summary-value">
                            {displayPhoneNumber}
                        </span>
                    </div>
                </div>
            </div>

            <div className="delivery-readonly-summary-section mt-3">
                <div className="delivery-readonly-summary-grid">
                    <div className="delivery-readonly-summary-item delivery-readonly-summary-item-full">
                        <span className="delivery-readonly-summary-label">{t("description")}</span>
                        <span className="delivery-readonly-summary-value">{description || "- - -"}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ReadonlyPickupSummary = ({
    email = "",
    phone,
    pickupTime = "",
}: ReadonlyPickupSummaryProps) => {
    const {
        dialCode = "",
        phoneNumber = "",
    } = phone || {};

    const {t} = useTranslation();

    const displayPhoneNumber = formatPhoneWithDialCode(dialCode, phoneNumber, "- - -");


    return (
        <div className="delivery-readonly-summary mb-3">
            <div className="delivery-readonly-summary-title mb-3">
                {t("pickupDetails")}
            </div>

            <div className="delivery-readonly-summary-section">
                <div className="delivery-readonly-summary-subtitle">
                    {t("contactInformationAtPickup")}
                </div>
                <div className="delivery-readonly-summary-grid">
                    <div className="delivery-readonly-summary-item">
                        <span className="delivery-readonly-summary-label">{t("email")}</span>
                        <span className="delivery-readonly-summary-value">{email || "- - -"}</span>
                    </div>
                    <div className="delivery-readonly-summary-item delivery-readonly-summary-item-full">
                        <span className="delivery-readonly-summary-label">{t("phoneNumber")}</span>
                        <span className="delivery-readonly-summary-value">
                            {displayPhoneNumber}
                        </span>
                    </div>
                </div>
            </div>

            {pickupTime && (
                <div className="delivery-readonly-summary-section mt-3">
                    <div className="delivery-readonly-summary-subtitle">{t("orderTiming")}</div>
                    <div className="delivery-readonly-summary-grid">
                        <div className="delivery-readonly-summary-item">
                            <span className="delivery-readonly-summary-label">{t("pickupTime")}</span>
                            <span className="delivery-readonly-summary-value">
                                {pickupTime}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
